import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { linkPage } from "@utils/router";
import { BiChevronRight } from "react-icons/bi";
import {
  FcBusinessContact,
  FcCalendar,
  FcGraduationCap,
  FcInspection,
  FcKindle,
  FcMoneyTransfer,
} from "react-icons/fc";

import moment from "moment";
import momentHijri from "moment-hijri";
import LogoHomePage from "@assets/images/logo-sipiat.png";
import MaleLogo from "@assets/images/avatar.png";
import FemaleLogo from "@assets/images/avatar-female.png";
import "@splidejs/react-splide/css";
import { useQuery } from "@tanstack/react-query";
import { SchoolService } from "@services/school.service";
import { HomeService } from "@services/home.service";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import { Mading, PrayerTimings } from "@services/dtos/home.dto";
import { useThemeStore } from "@stores/theme.store";
import { androidIsAvailableBiometric } from "@utils/android";

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { setAppBar } = useThemeStore((state) => state);
  const [pendingGetTime, setPendingGetTime] = useState<boolean>(true);
  const [countDownPrayer, setCountDownPrayer] = useState<string>(
    "--- jam --- menit menuju waktu ---"
  );
  const [prayerTime, setPrayerTime] = useState<PrayerTimings | null>(null);
  const [timezone, setTimezone] = useState<string>("");

  const daysOfHijri: { [key: string]: string } = {
    Monday: "Itsnain",
    Tuesday: "Tsulasa",
    Wednesday: "Arbi'aa'",
    Thursday: "Khomiis",
    Friday: "Jumu'ah",
    Saturday: "Sabt",
    Sunday: "Ahad",
  };
  const monthOfHijri: { [key: string]: string } = {
    1: "Muharram",
    2: "Safar",
    3: "Rabiul Awal",
    4: "Rabiul Akhir",
    5: "Jumadil Awal",
    6: "Jumadil Akhir",
    7: "Rajab",
    8: "Sya'ban",
    9: "Ramadhan",
    10: "Syawal",
    11: "Dzul Qa'dah",
    12: "Dzul Hijjah",
  };
  const kategori = "*";

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";
  const gender = dataNis?.data?.gender ?? "";

  const { data: dataUangSaku, isPending: isPendingUangSaku } = useQuery({
    queryKey: [HomeService.queries.GET_BILLS_AND_BALANCES, nis],
    queryFn: () => HomeService.billsAndBalances({ nis }),
  });

  const { data, isPending } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIOGRAPHY, nis],
    queryFn: () => SchoolService.studentBiography(nis),
  });

  const { data: dataMading, isPending: isPendingMading } = useQuery({
    queryKey: [HomeService.queries.GET_MADING, kategori, "5"],
    queryFn: () => HomeService.mading(kategori, "5"),
  });

  const hijriDate = momentHijri().format("iD");
  const hijriMonth = monthOfHijri[momentHijri().format("iM")];
  const hijriYear = momentHijri().format("iYYYY");

  const formatPrayer = (t: string | undefined) => moment(t, "HH:mm");
  const fajr = formatPrayer(prayerTime?.Fajr);
  const dhuhr = formatPrayer(prayerTime?.Dhuhr);
  const asr = formatPrayer(prayerTime?.Asr);
  const maghrib = formatPrayer(prayerTime?.Maghrib);
  const isha = formatPrayer(prayerTime?.Isha);

  const tzIndonesia =
    timezone === "Asia/Jakarta"
      ? "WIB"
      : timezone === "Asia/Makassar"
      ? "WITA"
      : timezone === "Asia/Jayapura"
      ? "WIT"
      : "---";

  useEffect(() => {
    // const setupUnreadInbox = setTimeout(() => androidUnreadInbox(), 1000);
    const setupBiometric = setTimeout(() => {
      localStorage.setItem("is-biometric", "false");
      androidIsAvailableBiometric();
    }, 1000);

    return () => {
      clearTimeout(setupBiometric);
      // clearTimeout(setupUnreadInbox);
    };
  }, []);

  useEffect(() => {
    setAppBar({ title: "Beranda", show: true, leading: "home" });

    const intervalId = setInterval(() => {
      // Prevent running if prayer times haven't been fetched yet
      if (
        !prayerTime?.Fajr ||
        !prayerTime?.Dhuhr ||
        !prayerTime?.Asr ||
        !prayerTime?.Maghrib ||
        !prayerTime?.Isha
      ) {
        setCountDownPrayer(`--- jam --- menit menuju waktu ---`);
        return;
      }
      const formatToMinutes = (m: moment.Moment) => m.hour() * 60 + m.minute();
      const getCurrentMinutes = formatToMinutes(moment());
      const prayerTimesArray = [
        { name: "Subuh", time: formatToMinutes(fajr) },
        { name: "Dzuhur", time: formatToMinutes(dhuhr) },
        { name: "Ashar", time: formatToMinutes(asr) },
        { name: "Maghrib", time: formatToMinutes(maghrib) },
        { name: "Isya", time: formatToMinutes(isha) },
      ];

      let nextPrayer = prayerTimesArray.find((p) => p.time > getCurrentMinutes);
      let timeDiff;

      if (nextPrayer) {
        timeDiff = nextPrayer.time - getCurrentMinutes;
      } else {
        // After Isya, so next is Subuh of next day
        nextPrayer = prayerTimesArray[0]; // Subuh
        timeDiff = 24 * 60 + nextPrayer.time - getCurrentMinutes;
      }
      const hours = Math.floor(timeDiff / 60);
      const minutes = timeDiff % 60;
      setCountDownPrayer(
        `${hours} jam ${minutes} menit menuju waktu ${nextPrayer.name}`
      );
      // if (nextPrayer) {
      //   const timeDiff = nextPrayer.isNextDay
      //     ? nextPrayer.time + 24 * 60 - getCurrentMinutes
      //     : nextPrayer.time - getCurrentMinutes;
      //   const hours = Math.floor(timeDiff / 60);
      //   const minutes = timeDiff % 60;
      //   setCountDownPrayer(
      //     `${hours} jam ${minutes} menit menuju waktu ${nextPrayer.name}`
      //   );
      // } else {
      //   setCountDownPrayer(`--- jam --- menit menuju waktu ---`);
      // }
      setPendingGetTime(false);
    }, 1000);
    setPendingGetTime(true);
    return () => {
      clearInterval(intervalId);
      setPendingGetTime(true);
    };
  }, [setAppBar, prayerTime, timezone]);

  const openDetailMading = (detailMading: Mading) => {
    navigate(linkPage.MADING_DETAIL, { state: detailMading });
  };

  useEffect(() => {
    // Ask Flutter for location again
    if (
      window.requestLocation &&
      typeof window.requestLocation.postMessage === "function"
    ) {
      window.requestLocation.postMessage("");
    }
    const handleFlutterLocation = async (event: any) => {
      const { lat, lng } = event.detail;
      try {
        const res = await fetch(
          `https://api.aladhan.com/v1/timings?latitude=${lat}&longitude=${lng}&method=20`
        );
        const data = await res.json();
        setPrayerTime(data.data.timings);
        setTimezone(data.data.meta.timezone);
      } catch (err) {
        console.error("Failed to fetch prayer times:", err);
      }
    };

    // Handle Flutter WebView
    window.addEventListener("flutter-location", handleFlutterLocation);

    // Handle regular browser
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            const res = await fetch(
              `https://api.aladhan.com/v1/timings?latitude=${latitude}&longitude=${longitude}&method=20`
            );
            const data = await res.json();
            setPrayerTime(data.data.timings);
            setTimezone(data.data.meta.timezone);
          } catch (err) {
            console.error("Failed to fetch prayer times:", err);
          }
        },
        (err) => {
          console.error("Browser Geolocation Error:", err.message);
        }
      );
    }

    return () => {
      window.removeEventListener("flutter-location", handleFlutterLocation);
    };
  }, [useLocation().pathname]);

  return (
    <>
      <div className="tw-rounded-b-2xl tw-pt-4">
        {/* Profile */}
        <div className="tw-flex tw-px-4">
          <div className="tw-grow tw-items-center flex">
            <div className="tw-tracking-wider tw-text-other-100 tw-max-w-64">
              <img src={LogoHomePage} alt="" className="tw-h-full tw-w-2/3" />
            </div>
          </div>

          <div className="tw-shrink-0">
            <Link
              to={linkPage.HOME_PROFILE}
              replace={true}
              onClick={() => navigate(linkPage.HOME_PROFILE)}
            >
              <img
                src={
                  isPending
                    ? MaleLogo
                    : data?.data?.foto === null || data?.data?.foto === ""
                    ? gender === "Pria" || gender === "PRIA" || gender === ""
                      ? MaleLogo
                      : FemaleLogo
                    : `https://sipiat2.pesantrenalirsyad2.org/assets/fotosiswa/${data?.data?.foto}`
                }
                alt={`Foto ${data?.data?.namasiswa}`}
                className="tw-rounded-full tw-object-cover tw-w-[50px] tw-h-[50px] md:tw-w-[65px] md:tw-h-[65px]"
              />
            </Link>
          </div>
        </div>
        {/* end of Profile */}

        {/* Prayer Time */}
        <div className="tw-mt-3 md:tw-mb-[3rem] tw-mt-[1rem]">
          <div className="tw-px-4 tw-pt-3 tw-pb-5">
            <div className="tw-py-3 tw-text-white">
              <div className="tw-flex tw-justify-between">
                <div className="tw-text-xs md:tw-text-base">
                  {moment().format("dddd") || "unknown"}, {moment().format("D")}{" "}
                  {moment().format("MMMM") || "unknown"}{" "}
                  {moment().format("YYYY")}
                </div>
                <div className="tw-text-xs md:tw-text-base">
                  {hijriDate} {hijriMonth} {hijriYear} H
                </div>
              </div>
              <div className="tw-flex tw-justify-between">
                <div className="tw-content-center tw-text-center tw-px-1"></div>
                <div className="tw-text-center">
                  <div className="tw-mt-8 tw-mb-3 tw-text-4xl tw-font-bold md:tw-text-[3rem]">
                    {moment().format("HH:mm")} {/* {tzIndonesia} */}
                  </div>
                  <div className="tw-text-xs md:tw-text-sm">
                    {/* <span>
                      {pendingGetTime
                        ? "- jam - menit menuju waktu ----"
                        : countDownPrayer}
                    </span> */}
                  </div>
                </div>
                <div className="tw-content-center tw-text-center tw-px-1"></div>
              </div>
            </div>
          </div>
        </div>
        {/* end of Prayer Time */}
      </div>

      {/* Pocket */}
      <div className="tw-p-4">
        <div
          className="bg-gradient tw-rounded-lg body-info"
          style={{ boxShadow: "2px 5px 8px rgba(0, 0, 0, 0.5)" }}
        >
          <div className="tw-bg-transparent tw-text-white tw-m-0 tw-px-4 tw-py-3 card-grid">
            <div
              className="tw-flex tw-justify-start tw-m-0 tw-shrink-0"
              onClick={() => navigate(linkPage.FINANCE_POCKET)}
            >
              {isPendingUangSaku ? (
                <div className="tw-py-2 tw-m-auto">
                  <Spiner className="" />
                </div>
              ) : (
                <>
                  <div className="tw-ps-2 tw-text-start tw-grow ">
                    <div className="tw-text-xs">Uang Saku</div>
                    <div className="tw-text-base tw-font-bold">
                      {toRp(dataUangSaku?.data?.saldo ?? 0)}
                    </div>
                  </div>
                  <BiChevronRight className="tw-self-center tw-text-[40px] tw-font-normal tw-text-gray-400 tw-text-end" />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* end of Pocket */}

      {/* Menu */}
      <div className="tw-p-4 tw-pb-0 tw-bg-other-0 menu">
        <div className="card-body-grid">
          <div className="card-grid">
            <div
              onClick={() => navigate(linkPage.INFO_BIOGRAPH)}
              className="tw-m-0"
            >
              <FcBusinessContact
                className="card-icon"
                style={{ background: "#d1c4e9" }}
              />
              <div className="tw-mt-0 card-name">Biodata</div>
            </div>
          </div>
          <div className="card-grid">
            <div
              onClick={() => navigate("/other-menu/academic")}
              className="tw-m-0"
            >
              <FcGraduationCap
                className="card-icon"
                style={{ background: "#cfdde4" }}
              />
              <div className="tw-mt-0 card-name">Sekolah</div>
            </div>
          </div>
          <div className="card-grid">
            <div
              onClick={() => navigate("/other-menu/parenting")}
              className="tw-m-0"
            >
              <FcKindle className="card-icon" style={{ background: "#eee" }} />
              <div className="tw-mt-0 card-name">Kepengasuhan</div>
            </div>
          </div>
          <div className="card-grid">
            <div
              onClick={() => navigate(linkPage.MENU_TAHFIDZ)}
              className="tw-m-0"
            >
              <FcInspection
                className="card-icon"
                style={{ background: "#eee" }}
              />
              <div className="tw-mt-0 card-name">Tahfidz</div>
            </div>
          </div>
          <div className="card-grid">
            <div
              onClick={() => navigate(linkPage.MENU_PERMIT)}
              className="tw-m-0"
            >
              <FcCalendar
                className="card-icon"
                style={{ background: "#f7dbd9" }}
              />
              <div className="tw-mt-0 card-name">Perizinan</div>
            </div>
          </div>
          <div className="card-grid">
            <div
              onClick={() => navigate(linkPage.MENU_FINANCE)}
              className="tw-m-0"
            >
              <FcMoneyTransfer
                className="card-icon"
                style={{ background: "#e1edd4" }}
              />
              <div className="tw-mt-0 card-name">Keuangan</div>
            </div>
          </div>
          <div className="card-grid">
            <div
              onClick={() => navigate(linkPage.MENU_PAYMENT)}
              className="tw-m-0"
            >
              <FcMoneyTransfer
                className="card-icon"
                style={{ background: "#e1edd4" }}
              />
              <div className="tw-mt-0 card-name">Pembayaran</div>
            </div>
          </div>
          <div className="card-grid">
            <div
              onClick={() => navigate(linkPage.MENU_RAPOR)}
              className="tw-m-0"
            >
              <FcKindle className="card-icon" style={{ background: "#eee" }} />
              <div className="tw-mt-0 card-name">Nilai</div>
            </div>
          </div>
        </div>
      </div>
      {/* end of Menu */}

      {/* Mading */}
      {isPendingMading ? (
        <div className="tw-mt-5 tw-py-3 tw-m-auto tw-text-center">
          <Spiner className="" />
        </div>
      ) : (dataMading?.data ?? []).length === 0 ? (
        <></>
      ) : (
        <>
          <div className="tw-pb-20 tw-bg-other-0">
            <div className="tw-py-3 tw-px-4 tw-flex tw-justify-between">
              <div className="tw-text-xl tw-font-medium md:tw-text-2xl">
                Informasi SIPiat
              </div>
              <div
                onClick={() => navigate("/mading/list")}
                className="float-right tw-text-xs tw-text-primary-ac tw-mt-1 md:tw-text-sm"
              >
                Lihat Semua
              </div>
            </div>
            <Splide
              options={{
                perPage: 1,
                arrows: false,
              }}
              className="slider-no-dots"
            >
              {isPendingMading
                ? [1, 2, 3].map((key) => (
                    <SplideSlide key={key}>
                      <div
                        className="card tw-rounded-l tw-mx-2 tw-text-center tw-shadow-l tw-d-flex"
                        style={{ height: 289 }}
                      >
                        <Spiner className="" />
                      </div>
                    </SplideSlide>
                  ))
                : (dataMading?.data ?? [])
                    .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
                    .slice(0, 3)
                    .map((mading, idx) => (
                      <SplideSlide
                        className="tw-ps-3 tw-pe-2 !tw-w-10/12 tw-mb-4"
                        key={idx}
                        onClick={() => openDetailMading(mading)}
                      >
                        <div className="tw-bg-other-100 tw-pb-3 tw-rounded-md tw-shadow-l tw-overflow-hidden">
                          <div
                            className="card tw-mb-1 tw-rounded-md tw-h-[150px]"
                            style={{
                              backgroundImage: `url(${mading.gambar})`,
                              backgroundSize: "cover",
                            }}
                          ></div>

                          <div className="tw-px-2 tw-mt-2 tw-text-xs tw-font-light tw-text-other-600">
                            {moment(mading.tanggal ?? Date.now()).format(
                              // "dddd, DD MMMM YYYY | HH:mm"
                              "dddd, DD MMMM YYYY"
                            )}
                          </div>
                          <div
                            className="tw-mb-0 tw-px-2 tw-mt-2 tw-text-md tw-font-medium"
                            style={{
                              overflowWrap: "break-word",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {mading.judul}
                          </div>
                        </div>
                      </SplideSlide>
                    ))}
            </Splide>
          </div>
        </>
      )}
      {/* end of Mading */}
    </>
  );
};
